import {FC, Fragment, useCallback, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {Grid, Header, Button, Popup, Icon, Placeholder, Ref, Message} from 'semantic-ui-react';
import {DragDropContext, Draggable, Droppable, DropResult} from 'react-beautiful-dnd';
import {useLocation} from 'react-router-dom';
import {
  forEach, find, transform, flatten, without, times,
  uniqueId, isEmpty, isMatch, pullAllWith, assign,
} from 'lodash';
import cx from 'classnames';
import {ActionsMenu} from 'apstra-ui-common';

import {Dashboard as DashboardType, Probe, Widget as WidgetType} from '../types';

import Widget from './Widget';
import WidgetModal, {WidgetModalProps} from './WidgetModal';

import './Dashboard.less';

type DashboardError = {
  type: 'widget';
  columnIndex: number;
  widgetIndex: number;
  errors: any[];
}

type DashboardProps = {
  dashboard: DashboardType;
  editable?: boolean;
  errors: DashboardError[];
  probes: Probe[];
};

const Dashboard: FC<DashboardProps> = observer(({
  dashboard,
  editable = false,
  errors,
  probes
}) => {
  const location = useLocation();

  const [isDragging, setIsDragging] = useState(false);
  const [widgetToId] = useState(() => new WeakMap<WidgetType, string>());

  const addWidget = useCallback((widget: WidgetType, columnIndex: number) => {
    const {grid} = dashboard;
    grid[columnIndex] = [...grid[columnIndex], widget];
  }, [dashboard]);

  const [widgetModalProps, setWidgetModalProps] = useState<Partial<WidgetModalProps> | null>(() => {
    const widgetToCreate = location.state?.widgetToCreate;
    if (!widgetToCreate) return null;
    return {
      open: true,
      mode: 'create',
      widget: widgetToCreate,
      fixedWidgetType: true,
      fixedStage: true,
      onSuccess: ({result: {widget}}) => {
        addWidget(widget, 0);
      }
    };
  });

  const getWidgetId = useCallback((widget: WidgetType) => {
    let id = widgetToId.get(widget);
    if (!id) {
      id = uniqueId('widget');
      widgetToId.set(widget, id);
    }
    return id;
  }, [widgetToId]);

  const idToWidget = useMemo(() => {
    return transform(flatten(dashboard.grid), (result, widget) => {
      result[getWidgetId(widget)] = widget;
    }, {});
  }, [dashboard.grid, getWidgetId]);

  const updateWidget = useCallback((widget: WidgetType, columnIndex: number, widgetIndex: number) => {
    const {grid} = dashboard;
    grid[columnIndex][widgetIndex] = widget;
    pullAllWith(errors, [{type: 'widget', columnIndex, widgetIndex}], isMatch);
  }, [dashboard, errors]);

  const removeWidget = useCallback((columnIndex: number, widgetIndex: number) => {
    const {grid} = dashboard;
    const widget = grid[columnIndex][widgetIndex];
    grid[columnIndex] = without(grid[columnIndex], widget);
    pullAllWith(errors, [{type: 'widget', columnIndex, widgetIndex}], isMatch);
  }, [dashboard, errors]);

  const onWidgetDragStart = useCallback(() => setIsDragging(true), []);

  const onWidgetDragEnd = useCallback(({draggableId, source, destination}: DropResult) => {
    const widget = idToWidget[draggableId];
    setIsDragging(false);
    if (
      !destination ||
      source.droppableId === destination.droppableId && source.index === destination.index
    ) return;
    const {grid} = dashboard;
    const sourceColumnIndex = Number(source.droppableId);
    const sourceWidgetIndex = source.index;
    const destinationColumnIndex = Number(destination.droppableId);
    const destinationWidgetIndex = destination.index;

    const widgetsToErrors = transform(errors, (result, error) => {
      if (error.type !== 'widget') return;
      const widget = grid[error.columnIndex][error.widgetIndex];
      if (!widget) return;
      result.set(widget, error);
    }, new Map());

    if (sourceColumnIndex === destinationColumnIndex) {
      const column = [...grid[sourceColumnIndex]];
      column.splice(sourceWidgetIndex, 1);
      column.splice(destinationWidgetIndex, 0, widget);
      grid[sourceColumnIndex] = column;
    } else {
      const sourceColumn = [...grid[sourceColumnIndex]];
      const destinationColumn = [...grid[destinationColumnIndex]];
      sourceColumn.splice(sourceWidgetIndex, 1);
      destinationColumn.splice(destinationWidgetIndex, 0, widget);
      grid[sourceColumnIndex] = sourceColumn;
      grid[destinationColumnIndex] = destinationColumn;
    }

    forEach(grid, (column, columnIndex) => {
      forEach(column, (widget, widgetIndex) => {
        const error = widgetsToErrors.get(widget);
        if (error) assign(error, {columnIndex, widgetIndex});
      });
    });
  }, [dashboard, errors, idToWidget]);

  return (
    <Fragment>
      <DragDropContext onDragStart={onWidgetDragStart} onDragEnd={onWidgetDragEnd}>
        <Grid className='iba-dashboard' columns='equal'>
          {dashboard.grid.map((column, columnIndex) =>
            <Droppable
              key={columnIndex}
              droppableId={String(columnIndex)}
              isDropDisabled={!editable}
            >
              {({innerRef, placeholder, droppableProps}, {isDraggingOver}) =>
                <Ref innerRef={innerRef}>
                  <Grid.Column
                    className={cx('iba-dashboard-column', {'dragging-over': isDraggingOver})}
                    {...droppableProps}
                  >
                    {column.map((widget, widgetIndex) => {
                      const widgetErrors = find(errors, {type: 'widget', columnIndex, widgetIndex})?.errors;
                      const widgetId = editable ? getWidgetId(widget) : columnIndex + '_' + widgetIndex;
                      return (
                        <Draggable
                          key={widgetId}
                          draggableId={widgetId}
                          index={widgetIndex}
                          isDragDisabled={!editable}
                        >
                          {({innerRef, draggableProps, dragHandleProps}) =>
                            <Ref innerRef={innerRef}>
                              <Grid className='iba-widget-container' {...draggableProps}>
                                <Grid.Column className='iba-widget-header' textAlign='center' width={16}>
                                  <Header
                                    size='small'
                                    color={!isEmpty(widgetErrors) ? 'red' : undefined}
                                    {...dragHandleProps}
                                  >
                                    {widget.label}
                                    {widget.description &&
                                      <Popup
                                        trigger={<sup><Icon name='help circle' /></sup>}
                                        content={widget.description}
                                        offset={[-13, 0]}
                                      />
                                    }
                                  </Header>
                                  {editable &&
                                    <div>
                                      <DashboardWidgetActions
                                        widget={widget}
                                        columnIndex={columnIndex}
                                        widgetIndex={widgetIndex}
                                        errors={widgetErrors}
                                        updateWidget={updateWidget}
                                        setWidgetModalProps={setWidgetModalProps}
                                        removeWidget={removeWidget}
                                      />
                                    </div>
                                  }
                                </Grid.Column>
                                <Grid.Column width={16}>
                                  {!isEmpty(widgetErrors) &&
                                    <Message
                                      error
                                      icon='warning sign'
                                      content='This widget contains errors. Edit the widget to fix the errors.'
                                    />
                                  }
                                  <Widget
                                    widget={widget}
                                  />
                                </Grid.Column>
                              </Grid>
                            </Ref>
                          }
                        </Draggable>
                      );
                    })}
                    {placeholder}
                    {editable &&
                      <AddWidgetPlaceholder
                        hidden={isDragging}
                        columnIndex={columnIndex}
                        setWidgetModalProps={setWidgetModalProps}
                        addWidget={addWidget}
                      />
                    }
                  </Grid.Column>
                </Ref>
              }
            </Droppable>
          )}
        </Grid>
      </DragDropContext>
      <WidgetModal
        open={false}
        onClose={() => setWidgetModalProps({open: false})}
        probes={probes}
        showCreateAnother={false}
        {...widgetModalProps}
      />
    </Fragment>
  );
});

type DashboardWidgetActionsProps = {
  widget: WidgetType;
  columnIndex: number;
  widgetIndex: number;
  errors?: any[];
  setWidgetModalProps: (props: Partial<WidgetModalProps>) => void;
  updateWidget: (widget: WidgetType, columnIndex: number, widgetIndex: number) => void;
  removeWidget: (columnIndex: number, widgetIndex: number) => void;
}

const DashboardWidgetActions: FC<DashboardWidgetActionsProps> = observer(({
  widget, columnIndex, widgetIndex, errors, setWidgetModalProps, updateWidget, removeWidget
}) => (
  <ActionsMenu
    size='tiny'
    floated='right'
    items={[
      {
        icon: 'edit',
        title: 'Edit',
        onClick: () => setWidgetModalProps({
          open: true,
          mode: 'update',
          widget,
          errors,
          onSuccess: ({result: {widget}}) => updateWidget(widget, columnIndex, widgetIndex),
        }),
      },
      {
        icon: 'trash',
        title: 'Remove',
        onClick: () => removeWidget(columnIndex, widgetIndex),
      },
    ]}
  />
));

type AddWidgetPlaceholderProps = {
  hidden: boolean;
  columnIndex: number;
  setWidgetModalProps: (props: Partial<WidgetModalProps>) => void;
  addWidget: (widget: WidgetType, columnIndex: number) => void;
};

const AddWidgetPlaceholder: FC<AddWidgetPlaceholderProps> = observer(({
  hidden, columnIndex, setWidgetModalProps, addWidget
}) => (
  <div className={cx('add-widget-placeholder', {hidden})}>
    <Placeholder className='add-widget-placeholder-background' fluid>
      {times(10, (index) => <Placeholder.Line key={index} />)}
    </Placeholder>
    <div className='add-widget-placeholder-actions'>
      <Button
        fluid
        color='teal'
        icon='add circle'
        labelPosition='left'
        content='Create New Widget'
        onClick={() => setWidgetModalProps({
          open: true,
          mode: 'create',
          onSuccess: ({result: {widget}}) => {
            addWidget(widget, columnIndex);
          }
        })}
      />
    </div>
  </div>
));

export default Dashboard;
